import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import api from "../api/api";
import { useAuth } from "./AuthContext";

export interface PlanUsageItem {
  current: number;
  max: number;
  percentage: number;
}

export interface SubscriptionData {
  plan: {
    id?: number;
    name: string;
    slug: string;
    status: string;
    trial_ends_at?: string;
    is_trialing: boolean;
  };
  usage: {
    projects: PlanUsageItem;
    users: PlanUsageItem;
    invoices_this_month: PlanUsageItem;
    storage_mb: PlanUsageItem;
  };
  features: Record<string, boolean>;
}

interface TenantContextType {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  fetchSubscription: () => Promise<void>;
  upgradePlan: (planSlug: string) => Promise<void>;
  isFeatureEnabled: (featureKey: string) => boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = async (): Promise<void> => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const res = await api.get("/subscription");
      const data = res.data?.data || res.data;
      setSubscription(data);
    } catch (err: any) {
      console.error("Failed to fetch subscription details:", err);
      setError(err.response?.data?.message || "Failed to fetch subscription");
    } finally {
      setLoading(false);
    }
  };

  const upgradePlan = async (planSlug: string): Promise<void> => {
    try {
      const res = await api.post("/subscription/upgrade", { plan_slug: planSlug });
      const data = res.data?.data || res.data;
      setSubscription(data);
    } catch (err: any) {
      console.error("Failed to upgrade plan:", err);
      if (err.response?.status === 501) {
        const message = err.response?.data?.message || "Plan upgrades are temporarily unavailable. Please contact support.";
        throw new Error(message);
      }
      throw err.response?.data || err;
    }
  };

  const isFeatureEnabled = (featureKey: string): boolean => {
    if (!subscription || !subscription.features) return true;
    return subscription.features[featureKey] ?? true;
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated]);

  return (
    <TenantContext.Provider
      value={{
        subscription,
        loading,
        error,
        fetchSubscription,
        upgradePlan,
        isFeatureEnabled,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
